import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "./Navbar";
import NavbarWithPushContent from "./NavbarWithPushContent";
import Footer from "./Footer";
import LoadingSpinner from "../Assets/LoadingSpinner";
import { NewsItem } from "../Types/Types";
import { useTranslation } from "react-i18next";

type ProductCategory = {
  _id: string;
  name: string;
  subcategories: { name: string; products: any[] }[];
  products: any[];
};

interface NewsDetailProps {
  products: ProductCategory[];
  lang: string;
}

const NewsDetail: React.FC<NewsDetailProps> = ({ products, lang }) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${apiUrl}/api/news/${id}`);
        if (res.ok) setItem(await res.json());
      } finally {
        setLoading(false);
      }
    })();
  }, [id, apiUrl]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-montserrat">
      <div className="hidden md:block">
        <NavbarWithPushContent products={products} lang={lang} />
      </div>
      <div className="block md:hidden">
        <Navbar products={products} lang={lang} />
      </div>

      <main className="flex-grow px-4 pt-28 md:pt-32 pb-16">
        {loading ? (
          <LoadingSpinner />
        ) : item ? (
          <article className="max-w-3xl mx-auto">
            <Link to="/news" className="text-brand-500 hover:text-brand-600 font-medium">
              ← {t("news.header")}
            </Link>
            <h1 className="mt-4 text-3xl sm:text-4xl font-bold text-brand-gray leading-tight">
              {item.title?.[lang]}
            </h1>
            {item.createdAt && (
              <p className="mt-2 text-sm text-gray-400">
                {new Date(item.createdAt).toLocaleDateString()}
              </p>
            )}
            {item.image && (
              <img
                src={item.image}
                alt={item.title?.[lang]}
                className="mt-6 w-full rounded-2xl shadow-lg object-cover"
              />
            )}
            <div
              className="prose max-w-none mt-8 text-gray-700 ql-editor"
              style={{ padding: 0 }}
              dangerouslySetInnerHTML={{ __html: item.content?.[lang] || "" }}
            />
          </article>
        ) : (
          <p className="text-center text-gray-600 text-lg mt-12">{t("news.empty")}</p>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default NewsDetail;
